import { useMemo } from "react";
import { useAccount, useReadContract, useReadContracts } from "wagmi";
import { type Address, formatUnits } from "viem";
import {
  ADDRESSES,
  V2_FACTORY_ABI,
  V2_PAIR_ABI,
  ERC20_ABI,
} from "../config/contracts";
import type { Farm } from "../components/farms/FarmCard";

/* ================================================================== */
/* useFarms — reads all V2 pairs from the factory on-chain             */
/* ================================================================== */

export function useFarms() {
  const { address: account } = useAccount();

  /* ---- Step 1: Get total pair count ---- */
  const { data: pairsLengthRaw, isLoading: isLoadingLength } = useReadContract({
    address: ADDRESSES.V2_FACTORY,
    abi: V2_FACTORY_ABI,
    functionName: "allPairsLength",
    query: { refetchInterval: 30_000 },
  });

  const pairsLength = Number(pairsLengthRaw ?? 0n);

  /* ---- Step 2: Get all pair addresses ---- */
  const pairAddressCalls = useMemo(() => {
    if (pairsLength === 0) return [];
    return Array.from({ length: pairsLength }, (_, i) => ({
      address: ADDRESSES.V2_FACTORY,
      abi: V2_FACTORY_ABI,
      functionName: "allPairs" as const,
      args: [BigInt(i)] as const,
    }));
  }, [pairsLength]);

  const {
    data: pairAddressResults,
    isLoading: isLoadingPairAddresses,
  } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: pairAddressCalls as any,
    query: {
      enabled: pairsLength > 0,
      staleTime: 60_000,
    },
  });

  const pairAddresses: Address[] = useMemo(() => {
    if (!pairAddressResults) return [];
    return pairAddressResults
      .map((r) => r.result as Address | undefined)
      .filter((addr): addr is Address => !!addr);
  }, [pairAddressResults]);

  /* ---- Step 3: For each pair, batch-read token0, token1, getReserves, totalSupply ---- */
  const pairDetailCalls = useMemo(() => {
    if (pairAddresses.length === 0) return [];
    const calls: {
      address: Address;
      abi: typeof V2_PAIR_ABI;
      functionName: string;
    }[] = [];

    for (const pairAddr of pairAddresses) {
      calls.push(
        { address: pairAddr, abi: V2_PAIR_ABI, functionName: "token0" },
        { address: pairAddr, abi: V2_PAIR_ABI, functionName: "token1" },
        { address: pairAddr, abi: V2_PAIR_ABI, functionName: "getReserves" },
        { address: pairAddr, abi: V2_PAIR_ABI, functionName: "totalSupply" }
      );
    }
    return calls;
  }, [pairAddresses]);

  const {
    data: pairDetailResults,
    isLoading: isLoadingPairDetails,
  } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: pairDetailCalls as any,
    query: {
      enabled: pairAddresses.length > 0,
      refetchInterval: 15_000,
    },
  });

  /* ---- Step 4: Extract token addresses to fetch their symbols/decimals ---- */
  const tokenAddressesSet = useMemo(() => {
    if (!pairDetailResults || pairAddresses.length === 0) return new Set<Address>();
    const set = new Set<Address>();
    for (let i = 0; i < pairAddresses.length; i++) {
      const base = i * 4;
      const t0 = pairDetailResults[base]?.result as Address | undefined;
      const t1 = pairDetailResults[base + 1]?.result as Address | undefined;
      if (t0) set.add(t0);
      if (t1) set.add(t1);
    }
    return set;
  }, [pairDetailResults, pairAddresses]);

  const tokenAddressList = useMemo(
    () => Array.from(tokenAddressesSet),
    [tokenAddressesSet]
  );

  const tokenInfoCalls = useMemo(() => {
    if (tokenAddressList.length === 0) return [];
    const calls: {
      address: Address;
      abi: typeof ERC20_ABI;
      functionName: string;
    }[] = [];
    for (const addr of tokenAddressList) {
      calls.push(
        { address: addr, abi: ERC20_ABI, functionName: "symbol" },
        { address: addr, abi: ERC20_ABI, functionName: "decimals" }
      );
    }
    return calls;
  }, [tokenAddressList]);

  const {
    data: tokenInfoResults,
    isLoading: isLoadingTokenInfo,
  } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: tokenInfoCalls as any,
    query: {
      enabled: tokenAddressList.length > 0,
      staleTime: Infinity,
    },
  });

  /* Build lookup maps for token symbol/decimals */
  const tokenSymbolMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!tokenInfoResults) return map;
    for (let i = 0; i < tokenAddressList.length; i++) {
      const sym = tokenInfoResults[i * 2]?.result as string | undefined;
      const addr = tokenAddressList[i].toLowerCase();
      if (sym) map.set(addr, sym);
    }
    /* WJASMY should display as JASMY for a nicer UX */
    map.set(ADDRESSES.WJASMY.toLowerCase(), "JASMY");
    return map;
  }, [tokenInfoResults, tokenAddressList]);

  const tokenDecimalsMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!tokenInfoResults) return map;
    for (let i = 0; i < tokenAddressList.length; i++) {
      const dec = tokenInfoResults[i * 2 + 1]?.result as number | undefined;
      const addr = tokenAddressList[i].toLowerCase();
      map.set(addr, dec ?? 18);
    }
    return map;
  }, [tokenInfoResults, tokenAddressList]);

  /* ---- Step 5: Get user LP balances for each pair ---- */
  const balanceCalls = useMemo(() => {
    if (!account || pairAddresses.length === 0) return [];
    return pairAddresses.map((pairAddr) => ({
      address: pairAddr,
      abi: V2_PAIR_ABI,
      functionName: "balanceOf" as const,
      args: [account] as const,
    }));
  }, [account, pairAddresses]);

  const {
    data: balanceResults,
    isLoading: isLoadingBalances,
  } = useReadContracts({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    contracts: balanceCalls as any,
    query: {
      enabled: !!account && pairAddresses.length > 0,
      refetchInterval: 15_000,
    },
  });

  /* ---- Step 6: Assemble Farm[] from all the on-chain data ---- */
  const farms: Farm[] = useMemo(() => {
    if (
      !pairDetailResults ||
      pairAddresses.length === 0 ||
      tokenSymbolMap.size === 0
    ) {
      return [];
    }

    const result: Farm[] = [];

    for (let i = 0; i < pairAddresses.length; i++) {
      const base = i * 4;
      const token0Addr = pairDetailResults[base]?.result as Address | undefined;
      const token1Addr = pairDetailResults[base + 1]?.result as Address | undefined;
      const reservesRaw = pairDetailResults[base + 2]?.result as
        | [bigint, bigint, number]
        | undefined;
      const totalSupplyRaw = pairDetailResults[base + 3]?.result as
        | bigint
        | undefined;

      if (!token0Addr || !token1Addr) continue;

      const sym0 =
        tokenSymbolMap.get(token0Addr.toLowerCase()) ?? "???";
      const sym1 =
        tokenSymbolMap.get(token1Addr.toLowerCase()) ?? "???";

      const dec0 = tokenDecimalsMap.get(token0Addr.toLowerCase()) ?? 18;
      const dec1 = tokenDecimalsMap.get(token1Addr.toLowerCase()) ?? 18;

      const reserve0 = reservesRaw ? reservesRaw[0] : 0n;
      const reserve1 = reservesRaw ? reservesRaw[1] : 0n;
      const totalSupply = totalSupplyRaw ?? 0n;

      /* Format reserves for display as TVL.
         Since we don't have a price oracle, show the combined reserves
         in human-readable form. If one side is JASMY (native), we can
         at least estimate TVL = 2 * reserve_JASMY. Otherwise show the
         sum of reserve values at a 1:1 proxy. */
      const r0Num = parseFloat(formatUnits(reserve0, dec0));
      const r1Num = parseFloat(formatUnits(reserve1, dec1));

      const isToken0Jasmy =
        token0Addr.toLowerCase() === ADDRESSES.WJASMY.toLowerCase();
      const isToken1Jasmy =
        token1Addr.toLowerCase() === ADDRESSES.WJASMY.toLowerCase();

      let tvlDisplay: string;
      if (isToken0Jasmy) {
        /* TVL ~ 2 * jasmy side */
        const tvl = r0Num * 2;
        tvlDisplay = formatTVL(tvl) + " JASMY";
      } else if (isToken1Jasmy) {
        const tvl = r1Num * 2;
        tvlDisplay = formatTVL(tvl) + " JASMY";
      } else {
        /* Neither token is JASMY — show both reserves */
        tvlDisplay = `${formatTVL(r0Num)} ${sym0} + ${formatTVL(r1Num)} ${sym1}`;
      }

      /* User LP balance */
      const userLpBalance =
        balanceResults && balanceResults[i]
          ? ((balanceResults[i].result as bigint) ?? 0n)
          : 0n;

      const userLpFormatted = formatUnits(userLpBalance, 18);
      const userLpNum = parseFloat(userLpFormatted);

      /* Pool share % */
      const _poolSharePct =
        totalSupply > 0n && userLpBalance > 0n
          ? (Number(userLpBalance) / Number(totalSupply)) * 100
          : 0;

      /* Determine if pool has any liquidity (active) */
      const hasLiquidity = reserve0 > 0n && reserve1 > 0n;

      result.push({
        id: i + 1,
        pair: `${sym0}-${sym1}`,
        token0Symbol: sym0,
        token1Symbol: sym1,
        apr: 0,
        multiplier: "1x",
        liquidity: tvlDisplay,
        earned: "0.00",
        staked: userLpNum > 0.0001 ? userLpNum.toFixed(4) : "0.00",
        depositFee: 0,
        contractAddress: pairAddresses[i],
        isActive: hasLiquidity,
      });
    }

    return result;
  }, [
    pairDetailResults,
    pairAddresses,
    tokenSymbolMap,
    tokenDecimalsMap,
    balanceResults,
  ]);

  const isLoading =
    isLoadingLength ||
    isLoadingPairAddresses ||
    isLoadingPairDetails ||
    isLoadingTokenInfo ||
    isLoadingBalances;

  return {
    farms,
    pairsLength,
    isLoading,
  };
}

/* ================================================================== */
/* Helper: format a numeric TVL into a readable string                 */
/* ================================================================== */

function formatTVL(value: number): string {
  if (value === 0) return "0";
  if (value >= 1_000_000_000) return (value / 1_000_000_000).toFixed(2) + "B";
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(2) + "M";
  if (value >= 1_000) return (value / 1_000).toFixed(2) + "K";
  if (value >= 1) return value.toFixed(2);
  return value.toFixed(6);
}
